import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import handleApiError from 'api/utils/handleApiError'

const usePropertyService = () => {
  const loadListedProperties = async (
    filterParams?: Record<string, unknown> | null
  ) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PROPERTY.LOAD_PROPERTIES, {
        params:
          filterParams && Object.keys(filterParams).length > 0
            ? { filters: filterParams }
            : undefined,
      })

      if (!response.data) {
        throw new Error()
      }

      return response.data.data
    } catch (err) {
      handleApiError('Failed to fetch listed properties', err)
    }
  }

  const loadListedProperty = async (id: string) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PROPERTY.LOAD_PROPERTIES, {
        params: { id },
      })

      if (!response.data) {
        throw new Error()
      }
      return response.data.data
    } catch (err) {
      handleApiError(`Failed to fetch property with ID: ${id}`, err)
    }
  }
  
  const loadMyProperties = async () => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PROPERTY.LOAD_MY_PROPERTIES)

      if (!response.data) {
        throw new Error()
      }

      return response.data.data
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { code?: string; role?: string; message?: string } }
      }
      if (axiosErr?.response?.data?.code === 'email_not_verified') {
        const gated = new Error(
          axiosErr.response.data.message || 'Email confirmation required'
        ) as Error & { code?: string; role?: string }
        gated.code = 'email_not_verified'
        gated.role = axiosErr.response.data.role || 'owner'
        throw gated
      }
      handleApiError('Failed to load your properties', err)
    }
  }

  /** Same row as public listing, only returned when the JWT user owns this property. */
  const loadMyPropertyById = async (propertyId: string) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PROPERTY.LOAD_MY_PROPERTIES, {
        params: { id: propertyId },
      })
      return response.data?.data ?? null
    } catch {
      return null
    }
  }

  const registerProperty = async (data: any) => {
    try {
      const response = await axiosInstance.post(
        ENDPOINTS.PROPERTY.CREATE_PROPERTY,
        data
      )
      
      if (!response.data) {
        throw new Error()
      }
      
      return response.data
    } catch (err) {
      handleApiError('Failed to create a new property', err)
    }
  }

  const verifyProperty = async (params: any) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.PROPERTY.VERIFY_PROPERTY, params)

      if (!response.data) {
        throw new Error()
      }

      return response.data
    } catch (err) {
      handleApiError('Failed to verify property', err)
    }
  }

  const loadTags = async () => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.PROPERTY.LOAD_TAGS)

      if (!response.data) {
        throw new Error()
      }

      return response.data.data
    } catch (err) {
      handleApiError('Failed to load tags', err)
    }
  }

  return {
    loadListedProperties,
    loadListedProperty,
    loadMyProperties,
    loadMyPropertyById,
    registerProperty,
    verifyProperty,
    loadTags,
  }
}

export default usePropertyService
